<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';

  export let el: HTMLElement | null = null;
  export let scrollerEl: HTMLDivElement | null = null;
  export let contentClass = '';
  export { className as class };
  let className = '';

  /** The orientation of the scrolling area */
  export let orientation: 'vertical' | 'horizontal' = 'vertical';
  /** Allow mouse dragging to scroll the content */
  export let mouseDrag = false;
  /** The color of the fade gradient | default from-ondark-base */
  export let gradientColor = 'from-ondark-base';
  /** Aligns scrollable area to the left, right, or center when smaller than the container | default start  */
  export let horizontalGravity: 'start' | 'end' | 'center' = 'start';
  /** The gap between items in the scrolling area | default gap-2 */
  export let gap: string = 'gap-2';
  /** A padding class to apply to the scrolling area | default p-1 */
  export let padding: string = 'p-1';
  /** Whether or not to show the scrollbar for the content | default false */
  export let scrollbars: boolean = false;
  /** The tailwind-unit size of the fade gradient | default 6 */
  export let fadeSize = 6;
  /** The size of the fade gradient at the start (top/left) | default fadeSize */
  export let fadeSizeStart = fadeSize;
  /** The size of the fade gradient at the end (bottom/right) | default fadeSize */
  export let fadeSizeEnd = fadeSize;

  const dispatch = createEventDispatcher();
  $: flexClass = orientation === 'horizontal' ? 'flex-row' : 'flex-col';
  $: sentinalClass = `pointer-events-none block relative shrink-0 bg-transparent z-1`;

  let startClass;
  let endClass;

  $: {
    if (orientation === 'horizontal') {
      startClass = `left-0 bottom-0 top-0 bg-gradient-to-r to-transparent ${gradientColor}`;
      endClass = `right-0 bottom-0 top-0 bg-gradient-to-l to-transparent ${gradientColor}`;
    } else {
      startClass = `left-0 right-0 top-0 bg-gradient-to-b to-transparent ${gradientColor}`;
      endClass = `left-0 right-0 bottom-0 bg-gradient-to-t to-transparent ${gradientColor}`;
    }
  }

  let observer: IntersectionObserver;
  let sentinelStart: HTMLDivElement;
  let sentinelEnd: HTMLDivElement;
  let gradientStart: HTMLSpanElement;
  let gradientEnd: HTMLSpanElement;
  let isAtStart = true;
  let isAtEnd = false;
  let firstRender = true;

  const setIntersect = (el: HTMLSpanElement, value: number) => {
    el.style.setProperty('--intersect', `${1 - value}`);
  };

  const handleFadeScrollCheck = (entries: IntersectionObserverEntry[]) => {
    isAtStart = false;
    isAtEnd = false;

    entries.forEach(entry => {
      if (entry.target === sentinelStart) {
        setIntersect(gradientStart, entry.intersectionRatio);
        if (entry.isIntersecting) {
          if (entry.intersectionRatio >= 0) {
            isAtStart = true;
            if (!firstRender) dispatch('atStart');
          }
        }
      }
      if (entry.target === sentinelEnd) {
        setIntersect(gradientEnd, entry.intersectionRatio);
        if (entry.isIntersecting) {
          if (entry.intersectionRatio >= 0) {
            isAtEnd = true;
            if (!firstRender) dispatch('atEnd');
          }
        }
      }
    });

    if (scrollerEl) {
      if (orientation === 'horizontal') {
        if (scrollerEl.clientWidth === scrollerEl.scrollWidth) {
          isAtStart = true;
          isAtEnd = true;
        } else if (scrollerEl.scrollLeft === 0) {
          isAtStart = true;
          isAtEnd = false;
        } else if (scrollerEl.scrollLeft === scrollerEl.scrollWidth - scrollerEl.clientWidth) {
          isAtStart = false;
          isAtEnd = true;
        }
      } else {
        if (scrollerEl.clientHeight === scrollerEl.scrollHeight) {
          isAtStart = true;
          isAtEnd = true;
        } else if (scrollerEl.scrollTop === 0) {
          isAtStart = true;
          isAtEnd = false;
        } else if (scrollerEl.scrollTop === scrollerEl.scrollHeight - scrollerEl.clientHeight) {
          isAtStart = false;
          isAtEnd = true;
        }
      }
    }
    firstRender = false;
  };

  onMount(() => {
    let options: IntersectionObserverInit = {
      root: scrollerEl,
      rootMargin: '0px',
      threshold: [0, 0.01, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.99, 1]
    };
    observer = new IntersectionObserver(handleFadeScrollCheck, options);
    observer.observe(sentinelStart);
    observer.observe(sentinelEnd);
    dispatch('observeStart');

    if (mouseDrag) {
      import('$lib/directives/inertiaScroll').then(({ inertiaScroll }) => {
        inertiaScroll(scrollerEl, { orientation });
      });
    }

    return () => {
      observer.unobserve(sentinelStart);
      observer.unobserve(sentinelEnd);
      dispatch('observeEnd');
    };
  });
</script>

<!-- 
  for vertical scrolling; use a fixed-height container
  outside of this component to contain the fade-scroll
 -->
<section bind:this={el} class="fade-scroll relative h-full w-full {className}" class:scrollbar-hide={!scrollbars}>
  <div
    bind:this={scrollerEl}
    class="fade-scroll__track flex h-full max-w-full grow {padding} {flexClass} {orientation}"
    class:overflow-x-scroll={orientation === 'horizontal'}
    class:overflow-y-scroll={orientation === 'vertical'}
    class:mx-auto={orientation === 'horizontal' && horizontalGravity === 'center'}
    class:mr-auto={orientation === 'horizontal' && horizontalGravity === 'start'}
    class:ml-auto={orientation === 'horizontal' && horizontalGravity === 'end'}
    class:scrollbar-hide={!scrollbars}
  >
    <!-- sentinel for checking if start is in view -->
    <div bind:this={sentinelStart} class="{sentinalClass} start {orientation === 'horizontal' ? 'w-8' : 'h-8'}"></div>
    <!-- content area -->
    <div
      class="fade-scroll__content {flexClass} {gap} {contentClass} flex shrink-0 grow {orientation === 'horizontal'
        ? `-mx-8`
        : `-my-8`}"
    >
      <slot />
    </div>
    <!-- sentinel for checking if end is in view -->
    <div bind:this={sentinelEnd} class="{sentinalClass} end {orientation === 'horizontal' ? 'w-8' : 'h-8'}"></div>
  </div>
  <!-- gradient overlay start -->
  <span
    bind:this={gradientStart}
    class:opacity-0={isAtStart}
    class="fade-scroll__fade fade-scroll__fade-start absolute transform-gpu {startClass} scrollbar-hide ease-smooth-out pointer-events-none transition-opacity"
    style={orientation === 'horizontal' ? `width: ${fadeSizeStart * 4}px;` : `height: ${fadeSizeStart * 4}px;`}
  />
  <!-- gradient overlay end -->
  <span
    bind:this={gradientEnd}
    class:opacity-0={isAtEnd}
    class="fade-scroll__fade fade-scroll__fade-end absolute transform-gpu {endClass} ease-smooth-out pointer-events-none transition-opacity"
    style={orientation === 'horizontal' ? `width: ${fadeSizeEnd * 4}px;` : `height: ${fadeSizeEnd * 4}px;`}
  />
</section>

<style lang="postcss">
  :global(.fade-scroll__track.active) {
    cursor: grabbing;
  }
  :global(.fade-scroll__track.active *) {
    pointer-events: none;
    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
  .fade-scroll__fade {
    opacity: var(--intersect, 0);
  }
</style>
